import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import PlayCircleOutline from '@mui/icons-material/PlayCircleOutline';
import StopCircleOutlined from '@mui/icons-material/StopCircleOutlined';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { getObjectId } from '@/helpers/object.id.helper';
import { getEntityRefFromFlow } from '@/helpers/entity-ref.helper';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { useCueCommandMutation } from '@/state/mutations/player/use-cue-command';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import {
  isFlowControlInBetweenSegmentKind,
  segmentKindToAnchor,
  type FlowControlSegmentKind,
} from '@/helpers/simulation-cue-engine.helper';
import { useFlowControlSegmentActive } from './use-flow-control-segment-active';
import {
  isFlowControlNextCueSegmentRow,
  resolveFlowControlExpandIdFromSegment,
} from '@/helpers/flow-control-playing-segment.helper';
import { useFlowControlMediaExpandStore } from '@/state/stores/use-flow-control-media-expand-store';
import type { FlowCueStateV1 } from '@/types/player/perform-control-v1.types';
import { useTranslations } from '@/i18n/use-translations';
import { PlayCueConfirmationModal } from '@/components/media/play-cue-confirmation-modal';
import { usePerformAdvanceCue } from '@/hooks/use-perform-advance-cue';
import { isSegmentFirstItemNextCue } from '@/helpers/is-segment-first-item-next-cue.helper';
import {
  resolveCurrentCueSheetPointer,
  resolveLiveNextCueTarget,
  resolveNextCuePreviewRow,
} from '@/helpers/resolve-next-cue-preview-row.helper';
import { entityRoomKeyFromEntityRef, usePerformControlWsStore } from '@/state/player/perform-control-ws.store';
import { useNextCueRecalculatingStore } from '@/state/player/next-cue-recalculating.store';
import { liveCueSheetToSimulationRows } from '@/helpers/live-cue-sheet-to-simulation-rows.helper';
import { useGetLiveContextStateQuery } from '@/state/queries/player/use-get-live-context-state';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useFlowControlNextCueSegmentExpandId } from './use-flow-control-next-cue-segment-expand-id';
import { isCueListSegmentIndicatorPending } from '@/helpers/resolve-next-cue-recalculating.helper';

/** Clear “starting…” chrome if cue projection never marks the segment active. */
const SEGMENT_PLAY_BUSY_TIMEOUT_MS = 8000;

export type SlotSegmentAnchor = 'flow_start' | 'flow_end' | 'slot';

export interface SlotRightSectionPlayIconProps {
  segmentAnchor: SlotSegmentAnchor;
  /** Distinguishes pre_flow vs flow_intro (and outro vs after_flow) within the same anchor. */
  segmentKind?: FlowControlSegmentKind;
  slotId?: string;
  /** In-between playout segments cannot be stopped manually from this control. */
  disableManualStop?: boolean;
  /** When auto-cue is inactive, invoked instead of cue commands (e.g. Control tab inline media toggle). */
  onActivate?: () => void;
  /** Parent-computed active state (e.g. in-between playout via currentStep). */
  segmentActiveOverride?: boolean;
  /** Tints the play icon success/green to signal the segment is fully preloaded (ready to play). */
  ready?: boolean;
  /** Section name in the jump confirmation (falls back to Play segment). */
  segmentTitle?: string;
}

function findFirstLiveCueItemForScope(
  cueState: FlowCueStateV1 | undefined,
  scope: FlowControlSegmentKind,
): { itemId: string; scope?: string; slotId?: string; cueItemId?: string } | null {
  if (!cueState?.items) {
    return null;
  }
  for (const [itemId, entry] of Object.entries(cueState.items)) {
    if (entry.scope === scope) {
      return {
        itemId,
        scope: entry.scope,
        slotId: entry.slotId,
        cueItemId: entry.cueItemId,
      };
    }
  }
  return null;
}

/** Play/stop control for flow Control tab slot rows when `perform_auto_cue` is enabled. */
export function SlotRightSectionPlayIcon({
  segmentAnchor,
  segmentKind,
  slotId,
  disableManualStop = false,
  onActivate,
  segmentActiveOverride,
  ready = false,
  segmentTitle,
}: SlotRightSectionPlayIconProps) {
  const t = useTranslations();
  const flow = useFlowStore((s) => s.flow);
  const { hasAccess: hasAutoCue, isLoading: featureLoading } = useFeatureAccess('perform_auto_cue');
  const flowId = flow?.origin ? getObjectId(flow.origin) : flow?.id;
  const entityRef = flowId ? getEntityRefFromFlow(flowId, flow?.liveContext) : undefined;
  const resolvedSlotId = slotId ? (getObjectId(slotId) ?? slotId) : undefined;
  const liveRunning = flow?.liveContext?.isRunning ?? false;
  const isSimulation = !liveRunning;

  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);
  const simulationPlaySegment = useFlowControlSimulationCueStore((s) => s.playSegment);
  const simulationStopSegment = useFlowControlSimulationCueStore((s) => s.stopSegment);
  const simulationPointer = useFlowControlSimulationCueStore((s) => s.pointer);
  const simulationOverridePointer = useFlowControlSimulationCueStore((s) => s.overridePointer);
  const simulationSelectedItemIds = useFlowControlSimulationCueStore((s) => s.selectedItemIds);
  const playedReturnPreviousKeys = useFlowControlSimulationCueStore((s) => s.playedReturnPreviousKeys);
  const setExpandId = useFlowControlMediaExpandStore((s) => s.setExpandId);

  const cueCommand = useCueCommandMutation();
  const { advanceCue } = usePerformAdvanceCue({
    flow: (flow ?? {}) as FlowDto,
    entityRef: entityRef ?? '',
    performMode: isSimulation ? 'simulation' : 'play',
  });

  const cueState = useLiveContextCueState(entityRef);
  const { data: liveContextStateFromGet } = useGetLiveContextStateQuery(entityRef, {
    enabled: liveRunning && !!entityRef,
  });
  const liveContextState = useLiveContextStateWithLivePlayState(entityRef, liveContextStateFromGet);
  const liveSheetRoomKey = entityRef ? entityRoomKeyFromEntityRef(entityRef) : null;
  const wsCueSheet = usePerformControlWsStore((s) =>
    liveSheetRoomKey ? s.cueSheetByRoom[liveSheetRoomKey] : undefined,
  );
  const liveCueSheet = wsCueSheet ?? liveContextState?.cueSheet;
  const liveSheetRows = useMemo(
    () => (liveRunning && liveCueSheet ? liveCueSheetToSimulationRows(liveCueSheet) : []),
    [liveCueSheet, liveRunning],
  );
  const cueRows = liveSheetRows.length > 0 ? liveSheetRows : simulationRows;
  const nextCueTarget = useMemo(() => {
    if (liveRunning) {
      return resolveLiveNextCueTarget(cueState, liveContextState?.playState?.lookAheadInsertResume);
    }
    const currentPointer = resolveCurrentCueSheetPointer(simulationRows, 'simulation', simulationPointer, cueState, {
      simulationOverridePointer,
    });
    return resolveNextCuePreviewRow(simulationRows, currentPointer, {
      cueOverrideActive: simulationOverridePointer != null,
      resumePointer: simulationPointer,
      selectedItemIds: simulationSelectedItemIds,
      flow: flow ?? undefined,
      playedReturnPreviousKeys,
    });
  }, [
    cueState,
    flow,
    liveContextState?.playState?.lookAheadInsertResume,
    liveRunning,
    playedReturnPreviousKeys,
    simulationOverridePointer,
    simulationPointer,
    simulationRows,
    simulationSelectedItemIds,
  ]);
  const firstItemIsNextCue =
    segmentKind != null && isSegmentFirstItemNextCue(cueRows, segmentKind, resolvedSlotId, nextCueTarget);
  const [playConfirmOpen, setPlayConfirmOpen] = useState(false);

  const isLiveSegmentActive = useFlowControlSegmentActive(segmentAnchor, segmentKind, slotId, segmentActiveOverride);
  const isSegmentActive = isLiveSegmentActive;
  const thisExpandId = resolveFlowControlExpandIdFromSegment(segmentKind, resolvedSlotId);
  const nextCueExpandId = useFlowControlNextCueSegmentExpandId();
  const isNextCueSegment = isFlowControlNextCueSegmentRow(thisExpandId, nextCueExpandId, isSegmentActive);
  const jumpIndicatorLatch = useNextCueRecalculatingStore((s) =>
    entityRef ? (s.latchByEntityRef[entityRef] ?? null) : null,
  );
  const indicatorPending = isCueListSegmentIndicatorPending({
    latch: jumpIndicatorLatch,
    segmentExpandId: thisExpandId,
    isSegmentPlaying: isSegmentActive,
    isNextCueSegment,
  });
  /** Stay busy until cue projection marks this segment active (HTTP often finishes earlier). */
  const [awaitingSegmentActive, setAwaitingSegmentActive] = useState(false);
  const isCommandPending = cueCommand.isPending;
  const isBusy = isCommandPending || awaitingSegmentActive || indicatorPending;
  const canInteractLive = hasAutoCue && !!entityRef && liveRunning && !isBusy && !featureLoading;
  const canInteractSimulation = isSimulation && simulationRows.length > 0 && segmentKind != null;
  const canInteract = canInteractSimulation || canInteractLive;

  useEffect(() => {
    if (isSegmentActive) {
      setAwaitingSegmentActive(false);
    }
  }, [isSegmentActive]);

  useEffect(() => {
    if (!awaitingSegmentActive || isSegmentActive) {
      return;
    }
    const timer = window.setTimeout(() => {
      setAwaitingSegmentActive(false);
    }, SEGMENT_PLAY_BUSY_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [awaitingSegmentActive, isSegmentActive]);

  const expandSegmentForPlay = () => {
    if (!segmentKind) {
      return;
    }
    const expandId = resolveFlowControlExpandIdFromSegment(segmentKind, resolvedSlotId);
    if (expandId) {
      setExpandId(expandId, 'playing');
    }
  };

  const mutateCueCommand = (body: Parameters<typeof cueCommand.mutate>[0]) => {
    cueCommand.mutate(body, {
      onError: () => {
        setAwaitingSegmentActive(false);
      },
    });
  };

  const confirmPlaySegment = () => {
    setPlayConfirmOpen(false);
    expandSegmentForPlay();
    if (isSimulation && canInteractSimulation && segmentKind) {
      simulationPlaySegment(segmentKind, resolvedSlotId);
      return;
    }
    playLiveSegment();
  };

  const requestPlaySegment = () => {
    if (firstItemIsNextCue) {
      expandSegmentForPlay();
      if (liveRunning) {
        setAwaitingSegmentActive(true);
      }
      advanceCue();
      return;
    }
    setPlayConfirmOpen(true);
  };

  const playLiveSegment = () => {
    if (!entityRef || !segmentKind) {
      return;
    }

    if (segmentKind === 'pre_flow' || segmentKind === 'flow_outro') {
      setAwaitingSegmentActive(true);
      mutateCueCommand({
        action: 'playSegment',
        entityRef,
        anchor: segmentKindToAnchor(segmentKind),
      });
      return;
    }

    if (segmentKind === 'session' || segmentKind === 'break' || isFlowControlInBetweenSegmentKind(segmentKind)) {
      if (!resolvedSlotId) {
        return;
      }
      setAwaitingSegmentActive(true);
      mutateCueCommand({
        action: 'playSegment',
        entityRef,
        anchor: 'slot',
        slotId: resolvedSlotId,
      });
      return;
    }

    const firstItem = findFirstLiveCueItemForScope(cueState, segmentKind);
    if (firstItem) {
      setAwaitingSegmentActive(true);
      mutateCueCommand({
        action: 'playMedia',
        entityRef,
        itemId: firstItem.itemId,
        cuePlayMode: 'jump',
        ...(firstItem.cueItemId ? { cueItemId: firstItem.cueItemId } : {}),
        ...(firstItem.scope ? { scope: firstItem.scope } : {}),
        ...(firstItem.slotId ? { slotId: firstItem.slotId } : {}),
      });
    }
  };

  const stopLiveSegment = () => {
    if (!entityRef || !segmentKind) {
      return;
    }
    if (segmentAnchor === 'slot' && !resolvedSlotId) {
      return;
    }
    mutateCueCommand({
      action: 'stopSegment',
      entityRef,
      anchor: segmentKindToAnchor(segmentKind),
      ...(segmentAnchor === 'slot' && resolvedSlotId ? { slotId: resolvedSlotId } : {}),
    });
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isSimulation && canInteractSimulation && segmentKind) {
      if (isSegmentActive) {
        if (!disableManualStop) {
          simulationStopSegment(segmentKind, resolvedSlotId);
        }
      } else {
        requestPlaySegment();
      }
      return;
    }

    if (!canInteractLive || !entityRef) {
      onActivate?.();
      return;
    }

    if (isLiveSegmentActive) {
      if (!disableManualStop) {
        stopLiveSegment();
      }
      return;
    }

    requestPlaySegment();
  };

  if (!hasAutoCue && !featureLoading && !isSimulation) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          minWidth: 0,
          alignSelf: 'stretch',
          boxSizing: 'border-box',
        }}
        onClick={(e) => {
          e.stopPropagation();
          onActivate?.();
        }}
      >
        <PlayCircleOutline
          sx={{
            fontSize: '1.5rem',
            width: '1.5rem',
            height: '1.5rem',
            color: 'action.active',
            cursor: onActivate ? 'pointer' : undefined,
          }}
        />
      </Box>
    );
  }

  const busyLabel = t('flows.detail.perform.slot.segmentStarting', undefined, 'Starting segment…');
  const label = isBusy
    ? busyLabel
    : isSegmentActive
      ? disableManualStop
        ? t('flows.detail.perform.slot.segmentPlaying', undefined, 'Segment playing')
        : t('flows.detail.perform.slot.stopSegment', undefined, 'Stop segment')
      : t('flows.detail.perform.slot.playSegment', undefined, 'Play segment');

  const stopDisabled = isSegmentActive && disableManualStop;
  const confirmTitle = segmentTitle?.trim() || t('flows.detail.perform.slot.playSegment', undefined, 'Play segment');

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          minWidth: 0,
          alignSelf: 'stretch',
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip title={label}>
          <span>
            <IconButton
              size="small"
              disabled={isBusy || stopDisabled || (!canInteract && !onActivate)}
              onClick={handleClick}
              aria-label={label}
              aria-busy={isBusy}
              sx={{ p: 0.5 }}
            >
              {isBusy ? (
                <CircularProgress size={20} color="inherit" />
              ) : isSegmentActive ? (
                <StopCircleOutlined
                  sx={{
                    fontSize: '1.5rem',
                    width: '1.5rem',
                    height: '1.5rem',
                    ...(stopDisabled
                      ? { color: 'action.disabled' }
                      : { color: isSimulation ? 'info.main' : 'error.main' }),
                  }}
                />
              ) : (
                <PlayCircleOutline
                  sx={{
                    fontSize: '1.5rem',
                    width: '1.5rem',
                    height: '1.5rem',
                    ...(ready ? { color: 'success.main' } : {}),
                  }}
                />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <PlayCueConfirmationModal
        open={playConfirmOpen}
        mediaTitle={confirmTitle}
        showCuePlayModes
        showPlayInsert={false}
        onJumpCue={confirmPlaySegment}
        onCancel={() => setPlayConfirmOpen(false)}
        isLoading={isBusy}
      />
    </>
  );
}
